
import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Printer as PrinterType } from "@/types/printer";

const PrinterManagement = () => {
  const { user } = useAuth();
  
  const [newPrinterName, setNewPrinterName] = useState("");
  const [newPrinterModel, setNewPrinterModel] = useState("");
  const [showAddPrinterDialog, setShowAddPrinterDialog] = useState(false);
  const [isAddingPrinter, setIsAddingPrinter] = useState(false);
  const [isLoadingPrinters, setIsLoadingPrinters] = useState(false);

  // TEMPORARILY USING MOCK DATA FOR PRINTERS
  const [printers, setPrinters] = useState<PrinterType[]>([
    {
      id: "1",
      name: "Sample Printer 1", 
      model: "Ender 3 V2",
      manufacturer: "Creality",
      build_plate_dimensions: "220x220x250",
      printer: "Sample Printer 1"
    },
    {
      id: "2",
      name: "Sample Printer 2",
      model: "Prusa i3 MK3S+", 
      manufacturer: "Prusa",
      build_plate_dimensions: "250x210x210",
      printer: "Sample Printer 2"
    }
  ]);

  const fetchPrinters = async () => {
    // Using mock data temporarily
    console.log("Using mock printer data");
  };

  const handleAddPrinter = async () => {
    if (!user || !newPrinterName.trim() || !newPrinterModel.trim()) return;

    setIsAddingPrinter(true);
    
    try {
      const newId = String(printers.length + 1);
      const newPrinter: PrinterType = {
        id: newId,
        name: newPrinterName.trim(),
        model: newPrinterModel.trim(),
        manufacturer: "",
        build_plate_dimensions: "",
        printer: newPrinterName.trim()
      };

      setPrinters([...printers, newPrinter]);

      // Reset form
      setNewPrinterName("");
      setNewPrinterModel("");
      setShowAddPrinterDialog(false);
      
      toast.success("Printer added successfully (temporary - not saved to database)");
    } catch (error) {
      console.error("Error adding printer:", error);
      toast.error("Failed to add printer");
    } finally {
      setIsAddingPrinter(false);
    }
  };

  const handleDeletePrinter = async (printerId: string) => {
    if (!user) return;

    try {
      setPrinters(printers.filter(p => p.id !== printerId));
      toast.success("Printer deleted successfully (temporary - not saved to database)");
    } catch (error) {
      console.error("Error deleting printer:", error);
      toast.error("Failed to delete printer");
    }
  };

  return (
    <div>
      <CardDescription className="mb-4">Manage your 3D printers (temporary mode - not saved to database)</CardDescription>
      
      {isLoadingPrinters ? (
        <p className="text-center py-8 text-muted-foreground">Loading printers...</p>
      ) : printers.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-muted-foreground mb-4">You haven't added any printers yet.</p>
          <Button
            onClick={() => setShowAddPrinterDialog(true)}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Your First Printer
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {printers.map((printer) => (
            <div key={printer.id} className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <p className="font-medium">{printer.name}</p>
                <p className="text-sm text-muted-foreground">{printer.model}</p>
              </div>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => handleDeletePrinter(printer.id)}
              >
                Delete
              </Button>
            </div>
          ))}
        </div>
      )}
      
      <Dialog open={showAddPrinterDialog} onOpenChange={setShowAddPrinterDialog}>
        <DialogTrigger asChild>
          <Button className="mt-4 w-full">
            <Plus className="h-4 w-4 mr-2" />
            Add Printer
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Printer</DialogTitle>
            <DialogDescription>
              Add details about your 3D printer (temporary - not saved to database).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="printer-name">Printer Name</Label>
              <Input 
                id="printer-name" 
                placeholder="My Awesome Printer" 
                value={newPrinterName}
                onChange={(e) => setNewPrinterName(e.target.value)}
              />
              <p className="text-sm text-muted-foreground">
                A friendly name for your printer
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="printer-model">Printer Model</Label>
              <Input 
                id="printer-model" 
                placeholder="e.g. Creality Ender 3" 
                value={newPrinterModel}
                onChange={(e) => setNewPrinterModel(e.target.value)}
              />
              <p className="text-sm text-muted-foreground">
                The model/make of your printer
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowAddPrinterDialog(false)}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleAddPrinter}
              disabled={isAddingPrinter || !newPrinterName.trim() || !newPrinterModel.trim()}
            >
              {isAddingPrinter ? "Adding..." : "Add Printer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PrinterManagement;
